import React, { useMemo, useState, useEffect } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Box, Link, Typography } from "@mui/material";
import CustomButton from "../../components/custom-button/CustomButton";
import CustomTable from "../../components/custom-table/CustomTable";
import CustomStatusBadge from "../../components/custom-status-badge/CustomStatusBadge";
import { useGetMilestonesListQuery } from "./GetMilestonesApiSlice";
import {
  filterByLanguages,
  formatDate,
} from "../../utility/functions/common-functions";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import { MILESTONE_PLACEHOLDER } from "../../utility/constants/common-constants";
import VideoModal from "../../components/custom-modal/VideoModal";
import CustomContentModal from "../../components/custom-content-modal/CustomContentModal";
import { useGetVideoContentForAdminQuery } from "./GetMilestonesApiSlice";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { useSnackbar, SnackbarKey } from "notistack";
import { MANAGE_MILESTONES_TABLE_HEADER } from "../../utility/constants/locale";
import DynamicContentIcons from "../../components/dynamic-content-icons/DynamicContentIcons";
import LanguageComponent from "../../components/langauge-component/LangaugeComponent";
import styles from "./ManageMilestones.module.scss";
import {
  ADMIN_MILESTONE_MULTILANGUAGE_LABEL,
  ENGLISH_LANGUAGE_CODE,
} from "../../../src/utility/constants/locale";

interface enabledLanguage {
  languageName: string;
  languageCode: string;
  active: boolean;
}

export interface MilestoneData {
  milestoneId: number;
  sourceMilestoneCode: string;
  milestoneTypeDisplayName: string;
  trimesterDisplayName: string;
  displayGroupDisplayName: string;
  milestoneDisplayName: string;
  longDescription: string;
  statusDisplayName: string;
  rewardPoints: number;
  createdDateTime: string;
  formattedDate: string;
  milestoneLearningMode: string;
  milestoneLearningUrls: string;
  educationalContentSourceId: number;
  educationalContentTypeId: number;
  isActive: boolean;
  languageIds: number[];
  enabledLanguages: enabledLanguage[];
  contentTypeCode: string;
}

const ManageMilestones: React.FC = () => {
  const langauge = "en";
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );

  const activeLanguages = useAppSelector(
    (state) => state.adminMasterData?.activeLanguages
  );
  const [selectedId, setSelectedId] = useState({
    taskCode: "",
    type: "",
    langauge: langauge,
  });
  const [refetchKey, setRefetchKey] = useState(0);
  const {
    data: educationalData,
    error: educationalDataError,
    isLoading: loadingContent,
    isSuccess,
  } = useGetVideoContentForAdminQuery(
    { ...selectedId, refetchKey, accessToken },
    { skip: !selectedId.taskCode || !selectedId.type }
  );
  const [contentType, setContentType] = useState("");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [videoContent, setVideoContent] = useState("");
  const [articleContent, setArticleContent] = useState("");
  const [subs, setSubs] = useState("");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [snackbarKey, setSnackbarKey] = useState<SnackbarKey | null>(null);

  const handleCellClick = (taskCode: string, type: string, url: string) => {
    if (type === "URL") {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setContentType(type);
    setSelectedId({ taskCode, type, langauge });
    setRefetchKey((prevKey) => prevKey + 1); // Increment the refetch key
  };

  useEffect(() => {
    if (snackbarKey !== null) {
      closeSnackbar(snackbarKey); // Close the specific Snackbar
      setSnackbarKey(null); // Reset the key
    }

    if (isSuccess) {
      if (educationalData.fullHtml) {
        setArticleContent(educationalData.fullHtml);
        setArticleModalOpen(true);
      } else if (educationalData.videoUrl) {
        setVideoContent(educationalData.videoUrl);
        setVideoModalOpen(true);
        setSubs(""); // Set subtitles if available
      }
    } else if (educationalDataError) {
      const key = enqueueSnackbar("Content link is not correct", {
        variant: "error",
      });
      setSnackbarKey(key);
    }
  }, [isSuccess, educationalData, educationalDataError]);

  const handleClose = () => {
    setVideoModalOpen(false);
    setArticleModalOpen(false);
    setArticleContent("");
    setVideoContent("");
  };

  const columns: MRT_ColumnDef<MilestoneData>[] = [
    {
      accessorKey: "sourceMilestoneCode",
      header: "Unique ID",
      filterFn: "contains",
      enableColumnFilter: true,
      Cell: ({ row, renderedCellValue }) => {
        const navigate = useNavigate();
        const handleClick = () => {
          navigate(`/admin/edit-milestone/${row.original.milestoneId}`, {
            state: { milestoneId: row.original.milestoneId },
          });
        };
        return (
          <Link
            onClick={handleClick}
            sx={{
              fontWeight: 500,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {renderedCellValue}
          </Link>
        );
      },
      size: 130,
    },
    {
      accessorKey: "milestoneDisplayName",
      header: "Milestone Name",
      filterFn: "contains",
      size: 150,
    },
    {
      accessorKey: "longDescription",
      header: "Description",
      filterFn: "contains",
      enableGlobalFilter: false,
      size: 180,
    },
    {
      accessorKey: "statusDisplayName",
      header: "Milestone Status",
      filterFn: "startsWith",
      enableGlobalFilter: false,
      Cell: ({ renderedCellValue }) => (
        <CustomStatusBadge status={String(renderedCellValue)} />
      ),
      size: 150,
    },
    {
      accessorKey: "trimesterDisplayName",
      header: "Trimester",
      filterFn: "contains",
      enableGlobalFilter: false,
      size: 140,
    },
    {
      accessorKey: "displayGroupDisplayName",
      header: "Week Group Assignment",
      filterFn: "contains",
      enableGlobalFilter: false,
      size: 200,
    },
    {
      accessorKey: "rewardPoints",
      header: "Points",
      filterFn: "contains",
      enableGlobalFilter: false,
      size: 110,
    },
    {
      accessorKey: "milestoneTypeDisplayName",
      header: "Milestone Type",
      filterFn: "contains",
      size: 130,
    },
    {
      accessorKey: "milestoneLearningMode",
      header: "Content Type",
      filterFn: "contains",
      enableGlobalFilter: false,
      Cell: ({ row, renderedCellValue }) => {
        const { sourceMilestoneCode, contentTypeCode, milestoneLearningUrls } =
          row.original;
        const handleClick = () =>
          handleCellClick(
            sourceMilestoneCode,
            contentTypeCode,
            milestoneLearningUrls
          );

        return (
          <Link
            sx={{
              fontWeight: 500,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={handleClick}
          >
            {contentTypeCode && (
              <DynamicContentIcons
                type={contentTypeCode}
                width={"12"}
                height={"12"}
                color="#000"
              />
            )}
            <span>{contentTypeCode ? renderedCellValue : "-"}</span>
          </Link>
        );
      },
      size: 130,
    },
    {
      accessorKey: "formattedDate",
      header: "Date Created",
      filterFn: "contains",
      enableGlobalFilter: false,
      size: 160,
    },
    {
      accessorKey: "enabledLanguages",
      header: "Language",
      filterFn: filterByLanguages,
      Cell: ({ renderedCellValue }) => {
        const enabledLanguages = Array.isArray(renderedCellValue)
          ? renderedCellValue
          : [];
        return <LanguageComponent enabledLanguages={enabledLanguages} />;
      },
      enableGlobalFilter: false,
      size: 130,
    },
  ];

  const { data, error, isLoading } = useGetMilestonesListQuery({ accessToken });
  const navigate = useNavigate();

  // Use useMemo to format data for the table
  const formatDataForTable = useMemo(() => {
    return (
      data &&
      data.milestones.map((task: MilestoneData) => {
        // Map over the activeLanguages array to get the status for each language
        const activeLanguagesForTask = activeLanguages.map((lang) => {
          // Check if the languageId exists in task.languageIds
          let languageExist = task.languageIds.includes(lang.languageId);
          if (lang.languageCode.toLowerCase() === ENGLISH_LANGUAGE_CODE) {
            languageExist = true;
          }
          return {
            languageName: lang.languageName,
            languageCode: lang.languageCode,
            active: languageExist, // Active if languageId is in task.languageIds, otherwise false
          };
        });

        // Return the updated task with enabled languages and formatted date
        return {
          ...task,
          enabledLanguages: activeLanguagesForTask,
          formattedDate: formatDate(task.createdDateTime),
        };
      })
    );
  }, [data]);

  if (isLoading) return <CustomSpinner />;
  if (error) return <div className="error">Error loading data</div>;

  const handleCreateMilestone = () => {
    navigate("/admin/create-milestone");
  };

  const renderTopToolbarCustomActions = () => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <CustomButton
        variant="contained"
        text="Create Milestone"
        onClick={handleCreateMilestone}
      />
    </Box>
  );

  return (
    <div className="ManageMilestones">
      <div
        className={`${styles.justifyContentEnd} ${styles.fontSize14}  ${styles.displayFlex}`}
      >
        {ADMIN_MILESTONE_MULTILANGUAGE_LABEL}:&nbsp;
        {activeLanguages.map((row, index) => (
          <span key={index} className={styles.langaugeSectionStyle}>
            {index < activeLanguages.length - 2
              ? row.languageName + ", " // Add comma for all except last two
              : index === activeLanguages.length - 1
              ? `and ${row.languageName}` // Space added before "and"
              : ` ${row.languageName}`}
            {/* Add space before last language */}
          </span>
        ))}
      </div>

      <CustomTable
        data={formatDataForTable || []}
        columns={columns}
        title={MANAGE_MILESTONES_TABLE_HEADER}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        globalFilterPlaceholder={MILESTONE_PLACEHOLDER}
        totalRecords={formatDataForTable?.length}
      />

      {loadingContent && <CustomSpinner />}
      {contentType === "videos" && (
        <VideoModal
          open={videoModalOpen}
          onClose={handleClose}
          videoUrl={videoContent}
          subtitle={subs}
          modalTitle={educationalData ? educationalData.title : ""}
          modalSummarry={educationalData ? educationalData.summary : ""}
        />
      )}
      {contentType === "articles" && (
        <CustomContentModal
          open={articleModalOpen}
          onClose={handleClose}
          htmlContent={articleContent}
          subtitle={subs}
          modalTitle={educationalData ? educationalData.title : ""}
        />
      )}
    </div>
  );
};

export default ManageMilestones;
